/**
 * useInternetConnectivity Hook
 *
 * Tracks the browser's online/offline status. Returns `{ isOnline }` reflecting
 * the current connectivity state, updated via the `window` `'online'` and
 * `'offline'` events.
 *
 * Conceptual source: Flutter `chat_screen.dart:626-644` —
 * `onInternetConnectivityChanged(connectionActive)` flips a flag and, when the
 * connection comes back, re-sends any failed text messages by walking
 * `_listItems` in reverse.
 *
 * The retry-on-reconnect side-effect is intentionally NOT handled here; it is
 * consumer-owned. `useChat` (Task 16) wires the retry loop by watching the
 * `isOnline` value returned from this hook.
 *
 * SSR-defensive: if `window` is undefined (e.g. during server-side rendering),
 * the hook defaults to `true` and skips listener registration.
 */
import { useEffect, useState } from 'react';

interface UseInternetConnectivityResult {
  isOnline: boolean;
}

export function useInternetConnectivity(): UseInternetConnectivityResult {
  const [isOnline, setIsOnline] = useState<boolean>(() => {
    if (typeof window === 'undefined') {
      return true;
    }
    return window.navigator.onLine;
  });

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return { isOnline };
}
